<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()

defineProps<{ monster?: NestMonsterType }>()
const emits = defineEmits(['start'])

async function handleStart(isTest: boolean) {
  if (envMode === 'prod') return ElMessage.warning('敬请期待')
  if (userStore.peakTimes - (8 - userStore.peakLevel) <= 0 && !isTest) return ElMessage.warning('挑战次数不足，请等待周日重置')
  ElMessageBox.confirm(
    `<p class="affix">确定进行【巅峰之路】<span class="info">${!isTest ? '挑战' : '模拟'}</span>吗？${
      !isTest ? '（失败或中途退出将消耗次数）' : ''
    }</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync({ type: 'startPeakRoad', data: { isTest } })
      if (!isTest) userStore.peakTimes--
      emits('start')
    })
    .catch(() => {})
}
function handleComplete() {
  ElMessageBox.confirm(`<p class="affix">确定以作弊形式通过【巅峰之路】吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'completePeakRoad' })
      userStore.peakLevel = 8
      ElMessage.success('成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="border-wrap eternal-tower">
    <h4 class="special">
      巅峰之路
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>巅峰之路说明</h4>
            <p>
              1. 巅峰之路为<span class="darkGold">暗金版本</span><span class="info">毕业</span>的玩家准备，要求<span class="info">在有限次数中</span
              >通过 <span class="grow">8</span><span class="myth">鎏金神殿BOSS</span>。
            </p>
            <p>2. 您需要在<span class="info">5分钟</span>的时间内通过挑战。</p>
            <p>3. 巅峰之路副本<span class="info">每周重置</span>，同步重置<span class="grow">层数</span>及<span class="grow">剩余次数</span>。</p>
            <p>4. 可通过<span class="info">模拟</span>挑战以熟悉副本强度，模拟时<span class="grow">失败不消耗次数</span>，同时成功不判定为通过。</p>
            <p>5. 完成挑战后开启<span class="awaken">二觉殿堂</span>，届时请前往主城进行二次觉醒。</p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>当前层级：{{ userStore.peakLevel + 1 }}</p>
    <p>剩余次数：{{ monster ? userStore.peakTimes - (8 - userStore.peakLevel) : 0 }}</p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="monster">{{ monster?.name }}</span>
            <span v-show="!monster">暂无战斗</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ monster?.name }}</h3>
          <p>血量：{{ monster?.hp }}</p>
          <p>防御：{{ monster?.def }}</p>
          <p>闪避率：{{ monster?.evs }}%</p>
          <p>抗爆率：{{ monster?.crtdef }}%</p>
          <template v-if="monster?.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(monster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button
        type="success"
        plain
        @click="handleStart(false)"
        :disabled="!(userStore.goldTempleRewardTimes === 0 && userStore.endlessTowerLevel > 49)"
      >
        {{ userStore.goldTempleRewardTimes === 0 && userStore.endlessTowerLevel > 49 ? '进入' : '通过鎏金神殿及无尽之塔50层解锁' }}
      </el-button>
      <el-button type="primary" plain @click="handleStart(true)" v-show="userStore.goldTempleRewardTimes === 0 && userStore.endlessTowerLevel > 40">
        模拟
      </el-button>
      <el-button
        type="warning"
        plain
        @click="handleComplete()"
        v-if="envMode === 'test'"
        v-show="userStore.goldTempleRewardTimes === 0 && userStore.endlessTowerLevel > 39"
      >
        作弊
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.eternal-tower {
  flex-flow: column;
}
</style>
